/**
 * Shared API fetch utilities for agent endpoints.
 */

import { config } from '../../config';
import type { AgentSession, AgentProfile, Message } from './agent-types';

type ApiError = {
  error: string;
  message?: string;
};

/**
 * Generic fetch wrapper with credentials and JSON handling.
 */
export async function fetchApi<T>(url: string, options?: RequestInit): Promise<T> {
  const response = await fetch(`${config.apiUrl}${url}`, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error((data as ApiError).error || 'Request failed');
  }

  return data as T;
}

/**
 * Fetch message history for a session.
 */
export function fetchSessionHistory(sessionId: string) {
  return fetchApi<{ messages: Message[] }>(`/api/agents/sessions/${sessionId}/history`);
}

/**
 * List sessions for the current user.
 */
export function fetchSessions(params?: { status?: string; limit?: number; offset?: number }) {
  const search = new URLSearchParams();
  if (params?.status) search.set('status', params.status);
  if (params?.limit) search.set('limit', String(params.limit));
  if (params?.offset) search.set('offset', String(params.offset));

  const query = search.toString();
  const url = `/api/agents/sessions${query ? `?${query}` : ''}`;
  return fetchApi<{ sessions: AgentSession[] }>(url);
}

/**
 * Create a new agent session.
 */
export function createSession(profileId: string) {
  return fetchApi<{ session: AgentSession }>('/api/agents/sessions', {
    method: 'POST',
    body: JSON.stringify({ profileId }),
  });
}

/**
 * Fetch all agent profiles.
 */
export function fetchProfiles() {
  return fetchApi<{ profiles: AgentProfile[] }>('/api/agents/profiles');
}

/**
 * End a session.
 */
export function endSession(sessionId: string) {
  return fetchApi<void>(`/api/agents/sessions/${sessionId}/end`, {
    method: 'POST',
  });
}
